import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import { formatMoney } from '../../utils/currency';
import {
  ACCOUNT_LABELS, ACCOUNT_TYPES, AccountType, PersonView, STATUS_COLORS, TRIGGER_LABELS, ReferralTrigger,
  personLabel, titleCase, useRewardsTenant,
} from './rewardsShared';

type ReferralRow = {
  _id: string;
  code: string;
  status: string;
  referrer?: PersonView | null;
  referee?: PersonView | null;
  referrerType?: AccountType;
  refereeType?: AccountType;
  rewardType?: 'wallet' | 'points';
  trigger?: ReferralTrigger;
  requiredCompletions?: number;
  completionsCount?: number;
  referrerReward?: number;
  refereeReward?: number;
  referrerCredited?: boolean;
  refereeCredited?: boolean;
  currency?: string;
  source?: string;
  qualifyingService?: string;
  qualifyingReferenceType?: string;
  qualifyingReferenceId?: string;
  qualifyingAmount?: number;
  failureReason?: string;
  rejectReason?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  completedAt?: string;
  expiresAt?: string;
};

const STATUSES = ['pending', 'processing', 'completed', 'failed', 'expired', 'rejected'];

function reward(row: ReferralRow, value?: number) {
  if (row.rewardType === 'points') return `${Math.floor(Number(value || 0))} pts`;
  return formatMoney(Number(value || 0), row.currency || undefined);
}

function dateText(value?: string) {
  return value ? new Date(value).toLocaleString() : '—';
}

export default function ReferralsPage() {
  const tenant = useRewardsTenant();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [referrerType, setReferrerType] = useState('all');
  const [refereeType, setRefereeType] = useState('all');
  const [rewardType, setRewardType] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = {
    page: page + 1,
    limit: pageSize,
    search: search.trim() || undefined,
    status: status === 'all' ? undefined : status,
    referrerType: referrerType === 'all' ? undefined : referrerType,
    refereeType: refereeType === 'all' ? undefined : refereeType,
    rewardType: rewardType === 'all' ? undefined : rewardType,
    from: from || undefined,
    to: to || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-referrals', tenant.scopeKey, params],
    queryFn: async () => {
      const res = await api.get('/admin/rewards/referrals', { params, headers: tenant.headers });
      return {
        rows: (res.data.data || []) as ReferralRow[],
        total: Number(res.data.meta?.total || 0),
      };
    },
    enabled: tenant.ready,
  });

  const { data: summary } = useQuery({
    queryKey: ['admin-referrals-summary', tenant.scopeKey],
    queryFn: async () => {
      const res = await api.get('/admin/rewards/referrals/summary', { headers: tenant.headers });
      return res.data.data as { byStatus: Record<string, number>; walletPaid: number; pointsPaid: number };
    },
    enabled: tenant.ready,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-referrals'] });
    queryClient.invalidateQueries({ queryKey: ['admin-referrals-summary'] });
    queryClient.invalidateQueries({ queryKey: ['admin-referral'] });
  };

  const columns: GridColDef<ReferralRow>[] = useMemo(() => [
    { field: 'createdAt', headerName: 'Date', width: 165, valueFormatter: (v) => dateText(v as string) },
    { field: 'referrer', headerName: 'Referrer', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.referrer) },
    { field: 'referee', headerName: 'Referee', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.referee) },
    {
      field: 'combo',
      headerName: 'Combination',
      width: 200,
      valueGetter: (_v, row) => `${row.referrerType ? ACCOUNT_LABELS[row.referrerType] : '—'} → ${row.refereeType ? ACCOUNT_LABELS[row.refereeType] : '—'}`,
    },
    { field: 'code', headerName: 'Code', width: 110 },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 110,
      valueGetter: (_v, row) => (row.trigger === 'signup' ? 'Signup' : `${row.completionsCount ?? 0}/${row.requiredCompletions ?? 1}`),
    },
    { field: 'referrerReward', headerName: 'Referrer reward', width: 130, valueGetter: (_v, row) => reward(row, row.referrerReward) },
    { field: 'refereeReward', headerName: 'Referee reward', width: 130, valueGetter: (_v, row) => reward(row, row.refereeReward) },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => <Chip size="small" color={STATUS_COLORS[p.row.status] || 'default'} label={titleCase(p.row.status)} />,
    },
  ], []);

  const reset = () => setPage(0);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Referrals & Points"
        title="Referrals"
        subtitle="Every referral across customers, partners and providers. Open a row to approve, reject or retry."
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Referrals' }]}
        actions={tenant.picker}
      />
      {tenant.notReady}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error, 'Could not load referrals')}</Alert>}

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {STATUSES.map((s) => (
            <Chip key={s} color={STATUS_COLORS[s]} variant="outlined" label={`${titleCase(s)} ${summary.byStatus[s] || 0}`} />
          ))}
          <Chip color="success" label={`Wallet paid ${formatMoney(summary.walletPaid)}`} />
          <Chip color="info" label={`Points paid ${summary.pointsPaid}`} />
        </Stack>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Name, phone, email or code" value={search} onChange={(e) => { setSearch(e.target.value); reset(); }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); reset(); }}>
          <MenuItem value="all">All statuses</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Referrer type" value={referrerType} onChange={(e) => { setReferrerType(e.target.value); reset(); }}>
          <MenuItem value="all">All</MenuItem>
          {ACCOUNT_TYPES.map((t) => <MenuItem key={t} value={t}>{ACCOUNT_LABELS[t]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Referee type" value={refereeType} onChange={(e) => { setRefereeType(e.target.value); reset(); }}>
          <MenuItem value="all">All</MenuItem>
          {ACCOUNT_TYPES.map((t) => <MenuItem key={t} value={t}>{ACCOUNT_LABELS[t]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Reward type" value={rewardType} onChange={(e) => { setRewardType(e.target.value); reset(); }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="wallet">Wallet</MenuItem>
          <MenuItem value="points">Points</MenuItem>
        </TextField>
        <TextField size="small" label="From" type="date" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="To" type="date" value={to} onChange={(e) => { setTo(e.target.value); reset(); }} InputLabelProps={{ shrink: true }} />
      </Box>

      <DataGrid
        rows={data?.rows || []}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(row) => row._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
        pageSizeOptions={[10, 20, 50, 100]}
        onRowClick={(p) => setSelectedId(p.row._id)}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, cursor: 'pointer' }}
      />

      {selectedId && (
        <ReferralDetail
          id={selectedId}
          headers={tenant.headers}
          scopeKey={tenant.scopeKey}
          onClose={() => setSelectedId(null)}
          onChanged={refresh}
        />
      )}
    </PageShell>
  );
}

function ReferralDetail({
  id,
  headers,
  scopeKey,
  onClose,
  onChanged,
}: {
  id: string;
  headers: Record<string, string>;
  scopeKey: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: ['admin-referral', scopeKey, id],
    queryFn: async () => (await api.get(`/admin/rewards/referrals/${id}`, { headers })).data.data as ReferralRow,
  });

  const action = useMutation({
    mutationFn: async (kind: 'approve' | 'retry' | 'reject') => {
      const body = kind === 'reject' ? { reason: reason.trim() } : {};
      return api.post(`/admin/rewards/referrals/${id}/${kind}`, body, { headers });
    },
    onSuccess: (_res, kind) => {
      setFeedback({ type: 'success', text: kind === 'reject' ? 'Referral rejected.' : 'Reward processed.' });
      setRejecting(false);
      setReason('');
      onChanged();
    },
    onError: (err) => setFeedback({ type: 'error', text: getApiErrorMessage(err, 'Action failed') }),
  });

  const canApprove = row && ['pending', 'expired', 'rejected'].includes(row.status);
  const canRetry = row?.status === 'failed';
  const canReject = row && ['pending', 'failed', 'expired'].includes(row.status);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Referral
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {feedback && <Alert severity={feedback.type} sx={{ mb: 2 }}>{feedback.text}</Alert>}
        {isLoading && <Typography color="text.secondary">Loading…</Typography>}
        {row && (
          <Stack spacing={1.25}>
            <Detail label="Status" value={titleCase(row.status)} />
            <Detail label="Code" value={row.code} />
            <Detail label="Referrer" value={`${personLabel(row.referrer)} (${row.referrerType ? ACCOUNT_LABELS[row.referrerType] : '—'})`} />
            <Detail label="Referrer phone" value={row.referrer?.phone || '—'} />
            <Detail label="Referee" value={`${personLabel(row.referee)} (${row.refereeType ? ACCOUNT_LABELS[row.refereeType] : '—'})`} />
            <Detail label="Referee phone" value={row.referee?.phone || '—'} />
            <Detail label="Applied via" value={titleCase(row.source)} />
            <Detail label="Reward type" value={row.rewardType === 'points' ? 'Points' : 'Wallet'} />
            <Detail label="Condition" value={row.trigger ? TRIGGER_LABELS[row.trigger] : '—'} />
            <Detail label="Completions" value={`${row.completionsCount ?? 0} / ${row.requiredCompletions ?? 1}`} />
            <Detail label="Referrer reward" value={`${reward(row, row.referrerReward)}${row.referrerCredited ? ' (paid)' : ''}`} />
            <Detail label="Referee reward" value={`${reward(row, row.refereeReward)}${row.refereeCredited ? ' (paid)' : ''}`} />
            {row.metadata?.referrerCapped === true && <Detail label="Note" value="Referrer reward skipped: monthly/lifetime cap reached" />}
            <Detail label="Qualifying service" value={titleCase(row.qualifyingService)} />
            <Detail label="Qualifying reference" value={row.qualifyingReferenceType ? `${row.qualifyingReferenceType} ${row.qualifyingReferenceId || ''}` : '—'} />
            <Detail label="Qualifying amount" value={row.qualifyingAmount != null ? formatMoney(row.qualifyingAmount, row.currency || undefined) : '—'} />
            {row.failureReason && <Detail label="Failure reason" value={row.failureReason} />}
            {row.rejectReason && <Detail label="Reject reason" value={row.rejectReason} />}
            <Detail label="Created" value={dateText(row.createdAt)} />
            <Detail label="Expires" value={dateText(row.expiresAt)} />
            <Detail label="Completed" value={dateText(row.completedAt)} />
            {rejecting && (
              <TextField
                autoFocus
                multiline
                minRows={2}
                label="Reason for rejection"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {canReject && !rejecting && <Button color="error" onClick={() => setRejecting(true)}>Reject</Button>}
        {rejecting && (
          <>
            <Button onClick={() => setRejecting(false)}>Cancel</Button>
            <Button color="error" variant="contained" disabled={!reason.trim() || action.isPending} onClick={() => action.mutate('reject')}>
              Confirm reject
            </Button>
          </>
        )}
        {!rejecting && canRetry && (
          <Button variant="outlined" disabled={action.isPending} onClick={() => action.mutate('retry')}>Retry payout</Button>
        )}
        {!rejecting && canApprove && (
          <Button variant="contained" color="success" disabled={action.isPending} onClick={() => action.mutate('approve')}>
            Approve & pay now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  );
}
